import React from 'react'
import { Cookie } from 'lucide-react'

const CookiePolicyPage: React.FC = () => {
  const lastUpdated = "December 2025"

  return (
    <div className="font-outfit text-ink-green-ink dark:text-ink-green-ink bg-ink-green-canvas dark:bg-ink-green-canvas min-h-screen">
      {/* Main content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="relative flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-28 pb-6">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-1.5 bg-ink-green-accent/10 text-ink-green-accent rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6 border border-ink-green-accent/20">
                <Cookie className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
                Website Cookies & Tracking
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium tracking-[-.03em] text-ink-green-ink dark:text-ink-green-ink mb-4 sm:mb-6 leading-tight">
                Cookie{' '}
                <span className="text-ink-green-accent">Policy</span>
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-ink-green-ink-soft dark:text-ink-green-ink-soft leading-[1.55] max-w-2xl mx-auto">
                Learn about how we use cookies and similar technologies to improve your browsing experience on our website.
              </p>
              <p className="text-xs sm:text-sm text-ink-green-ink-soft dark:text-ink-green-ink-soft mt-3 sm:mt-4">
                Last updated: {lastUpdated}
              </p>
            </div>
          </div>
        </section>

        {/* Cookie Policy Content */}
        <section className="relative pt-4 pb-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <div className="bg-ink-green-surface dark:bg-ink-green-surface rounded-panel border border-ink-green-edge p-8 md:p-12">
              <div className="text-sm sm:text-base text-ink-green-ink-soft dark:text-ink-green-ink-soft leading-[1.55] space-y-6 sm:space-y-8">

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">1. What Are Cookies</h2>
                  <p>Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better browsing experience by remembering your preferences and analyzing how you use our site.</p>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">2. Types of Cookies We Use</h2>

                  <div className="space-y-4 sm:space-y-6">
                    <div className="bg-ink-green-canvas dark:bg-ink-green-canvas rounded-panel p-4 sm:p-6 border border-ink-green-edge">
                      <h3 className="text-base sm:text-lg font-medium tracking-[-.03em] text-ink-green-ink dark:text-ink-green-ink mb-2 sm:mb-3">Essential Cookies</h3>
                      <p>These cookies are necessary for the website to function properly. They enable basic features like page navigation and access to secure areas of the website.</p>
                      <ul className="list-disc pl-6 mt-3 space-y-1">
                        <li>Session management</li>
                        <li>Security authentication</li>
                        <li>Load balancing</li>
                      </ul>
                    </div>

                    <div className="bg-ink-green-canvas dark:bg-ink-green-canvas rounded-panel p-4 sm:p-6 border border-ink-green-edge">
                      <h3 className="text-base sm:text-lg font-medium tracking-[-.03em] text-ink-green-ink dark:text-ink-green-ink mb-2 sm:mb-3">Performance Cookies</h3>
                      <p>These cookies collect information about how visitors use our website, helping us improve site performance and user experience.</p>
                      <ul className="list-disc pl-6 mt-3 space-y-1">
                        <li>Google Analytics</li>
                        <li>Page load times</li>
                        <li>Error reporting</li>
                      </ul>
                    </div>

                    <div className="bg-ink-green-canvas dark:bg-ink-green-canvas rounded-panel p-4 sm:p-6 border border-ink-green-edge">
                      <h3 className="text-base sm:text-lg font-medium tracking-[-.03em] text-ink-green-ink dark:text-ink-green-ink mb-2 sm:mb-3">Functional Cookies</h3>
                      <p>These cookies remember your preferences and choices to provide enhanced, personalized features.</p>
                      <ul className="list-disc pl-6 mt-3 space-y-1">
                        <li>Language preferences</li>
                        <li>Theme settings (dark/light mode)</li>
                        <li>Form data retention</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">3. Third-Party Cookies</h2>
                  <p className="mb-3 sm:mb-4">We may use third-party services that set cookies on your device:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li><strong>Google Analytics:</strong> For website traffic analysis and user behavior insights</li>
                    <li><strong>Cloudflare:</strong> For content delivery and security</li>
                    <li><strong>Social Media:</strong> If you interact with social media content on our site</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">4. Managing Your Cookie Preferences</h2>
                  <p className="mb-3 sm:mb-4">You can control and manage cookies in several ways:</p>

                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base sm:text-lg font-medium tracking-[-.03em] text-ink-green-ink dark:text-ink-green-ink mb-2">Browser Settings</h3>
                      <p>Most web browsers allow you to control cookies through their settings. You can:</p>
                      <ul className="list-disc pl-6 mt-2 space-y-1">
                        <li>Block all cookies</li>
                        <li>Block third-party cookies only</li>
                        <li>Delete cookies when you close your browser</li>
                        <li>Have your browser prompt you before accepting cookies</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-base sm:text-lg font-medium tracking-[-.03em] text-ink-green-ink dark:text-ink-green-ink mb-2">Opt-Out Tools</h3>
                      <p>For analytics cookies, you can opt out using:</p>
                      <ul className="list-disc pl-6 mt-2 space-y-1">
                        <li>Google Analytics Opt-out Browser Add-on</li>
                        <li>Network Advertising Initiative opt-out page</li>
                        <li>Your Online Choices (EU residents)</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">5. Cookie Retention</h2>
                  <p>Different cookies have different retention periods:</p>
                  <ul className="list-disc pl-6 mt-4 space-y-2">
                    <li><strong>Session Cookies:</strong> Deleted when you close your browser</li>
                    <li><strong>Persistent Cookies:</strong> Remain for a set period (typically 30 days to 2 years)</li>
                    <li><strong>Analytics Cookies:</strong> Usually expire after 2 years</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">6. Impact of Disabling Cookies</h2>
                  <p>While you can browse our website with cookies disabled, some features may not work properly:</p>
                  <ul className="list-disc pl-6 mt-4 space-y-2">
                    <li>Personalized content and recommendations</li>
                    <li>Form auto-completion</li>
                    <li>Remember your preferences</li>
                    <li>Analytics and site improvement insights</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">7. Contact Us</h2>
                  <p className="mb-3 sm:mb-4">If you have questions about our cookie policy:</p>
                  <div className="bg-ink-green-canvas dark:bg-ink-green-canvas rounded-panel p-4 sm:p-6 border border-ink-green-edge">
                    <p><strong>Email:</strong> privacy@novahass.partners</p>
                    <p><strong>Address:</strong> 7 Bell Yard, London, WC2A 2JR</p>
                    <p><strong>Subject Line:</strong> Cookie Policy Inquiry</p>
                  </div>
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-medium tracking-[-.03em] text-ink-green-heading-soft mb-3 sm:mb-4">8. Updates to This Policy</h2>
                  <p>We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.</p>
                </div>

              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default CookiePolicyPage
